/**
 * @deprecated
 */
import clsx from 'clsx';
import { Property } from 'csstype';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { TableCell, TableHeader } from '../table';
import { SmartTableCell } from './smart-table-cell/SmartTableCell';
import styles from './SmartTable.module.scss';
import { SmartTableCellProps, SmartTableDataItem } from './SmartTable.types';
import {
  TableColumnsEditor,
  TableColumnsEditorTranslations,
} from './table-columns-editor/TableColumnsEditor';

export type StringKeys<T> = Extract<keyof T, string>;

export type SmartTableColumn<T> = {
  [ColumnKey in StringKeys<T>]: {
    key: ColumnKey;
    label: string;
    alignment?: Property.TextAlign;
    disableHiding?: boolean;
    hiddenByDefault?: boolean;
    sticky?: boolean;
    render?: (value: T[ColumnKey], item: T) => ReactNode;
    renderHeader?: () => ReactNode;
    renderCell?: (props: SmartTableCellProps<T>) => ReactNode;
  };
}[StringKeys<T>];

export interface SmartTableProps<T> {
  className?: string;
  withBodyRowHover?: boolean;
  isHeaderSticky?: boolean;
  editableColumns?: boolean;
  columns: SmartTableColumn<T>[];
  data: SmartTableDataItem<T>[];
  headerTitle?: { title: string; colSpan?: number }[];
  alignment?: Property.TextAlign;
  i18nTranslations?: {
    editColumnTooltip: string;
  };
  onActiveColumnsChange?: (newColumns: StringKeys<T>[]) => void;
  renderActionColumn?: (item: SmartTableDataItem<T>) => ReactNode;
  onRowClick?: (item: SmartTableDataItem<T>) => void;
  activeColumnChangeHandler?: (
    activeColumnKeys: StringKeys<T>[],
    name: StringKeys<T>,
    isChecked: boolean
  ) => StringKeys<T>[];
  useTableScroll?: boolean;
}

const getColumnKeys = <T,>(columns: SmartTableProps<T>['columns']) =>
  columns.filter(c => !c.hiddenByDefault).map(c => c.key);

export const SmartTable = <T extends {}>({
  withBodyRowHover = true,
  className,
  isHeaderSticky = false,
  editableColumns = false,
  columns,
  data,
  headerTitle,
  alignment = 'left',
  i18nTranslations,
  onActiveColumnsChange,
  onRowClick,
  renderActionColumn,
  activeColumnChangeHandler,
  useTableScroll = false,
}: SmartTableProps<T>) => {
  const ref = useRef<HTMLTableElement>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const columnKeys = getColumnKeys(columns);
  const [activeColumnKeys, setActiveColumnKeys] = useState(columnKeys);
  const [firstColumnWidth, setFirstColumnWidth] = useState<number | null>(null);

  useEffect(() => {
    setActiveColumnKeys(getColumnKeys(columns));
  }, [columnKeys.toString()]);

  const handleActiveColumnsChange = (
    name: StringKeys<T>,
    isChecked: boolean
  ) => {
    let keys: StringKeys<T>[];

    if (activeColumnChangeHandler) {
      const stringKeys = activeColumnChangeHandler(
        activeColumnKeys,
        name,
        isChecked
      );
      keys = stringKeys;
    } else {
      keys = isChecked
        ? [...activeColumnKeys, name]
        : activeColumnKeys.filter(key => key !== name);
    }

    setActiveColumnKeys(keys);
    onActiveColumnsChange?.(keys);
  };

  useEffect(() => {
    if (ref.current) {
      const firstColumn = ref.current.querySelector('th:first-child');

      if (firstColumn) {
        const columnWidth = firstColumn.getBoundingClientRect().width;
        setFirstColumnWidth(columnWidth);
      }
    }
  }, []);

  useEffect(() => {
    const table = ref.current as unknown as HTMLTableElement;
    const isScrollable =
      table &&
      table!.parentElement!.clientWidth < table!.parentElement!.scrollWidth;
    setHasScrollbar(isScrollable);
  }, [activeColumnKeys]);

  const activeColumns = columns.filter(column =>
    activeColumnKeys.includes(column.key)
  );
  const isFirstColumnSticky = useMemo(() => {
    return !!columns[0].sticky;
  }, [columns]);

  const tableHead = activeColumns.map(column => {
    const content = column.renderHeader ? column.renderHeader() : column.label;
    const columnAlignment = column.alignment || alignment;
    return column.sticky ? (
      <TableHeader
        className={clsx(styles.stickyTableHeader, {
          [styles.stickyTableHeaderBorder]: hasScrollbar,
        })}
        alignment={columnAlignment}
        key={column.key}
        isHoverEnabled={editableColumns}
        data-smart-table-sticky-header
      >
        {content}
      </TableHeader>
    ) : (
      <TableHeader
        key={column.key}
        alignment={columnAlignment}
        isHoverEnabled={editableColumns}
      >
        {content}
      </TableHeader>
    );
  });

  const renderContent = (content: ReactNode) =>
    useTableScroll ? (
      <div
        className={clsx(styles.smartTableScrollRoot, {
          [styles.smartTableScrollRootSticky]: isFirstColumnSticky,
        })}
        style={
          isFirstColumnSticky && firstColumnWidth !== null
            ? ({ '--first-column-width': `${firstColumnWidth}px` } as React.CSSProperties)
            : undefined
        }
      >
        {content}
      </div>
    ) : (
      <>{content}</>
    );

  return renderContent(
    <table
      className={clsx(styles.table, className, {
        [styles.tableWithBodyRowHover]: withBodyRowHover,
        [styles.tableHeaderSticky]: isHeaderSticky,
      })}
      ref={ref}
      data-smart-table
    >
      <thead>
        {headerTitle && (
          <tr>
            {headerTitle.map(header => (
              <th
                className={styles.tableHeaderTitle}
                key={header.title}
                colSpan={header.colSpan || columns.length}
              >
                {header.title}
              </th>
            ))}
          </tr>
        )}
        <tr>
          {tableHead}

          {editableColumns && (
            <TableColumnsEditor<T>
              columns={columns}
              activeColumnKeys={activeColumnKeys}
              onActiveColumnToggle={handleActiveColumnsChange}
              i18nTranslations={
                i18nTranslations as TableColumnsEditorTranslations
              }
            />
          )}
          {renderActionColumn && <TableHeader />}
        </tr>
      </thead>
      <tbody>
        {data.map(item => (
          <tr
            className={clsx(styles.styledTableRow, {
              [styles.styledTableRowClickable]: !!onRowClick,
            })}
            data-table-data-row
            data-testid="table-data-row"
            data-smart-table-row
            onClick={onRowClick ? () => onRowClick(item) : undefined}
            key={item.key}
          >
            {activeColumns.map(column => {
              const columnAlignment = column.alignment || alignment;
              return (
                column.renderCell?.({
                  item,
                  column,
                  hasScrollbar,
                  alignment: columnAlignment,
                }) || (
                  <SmartTableCell
                    key={column.key}
                    item={item}
                    column={column}
                    alignment={columnAlignment}
                    hasScrollbar={hasScrollbar}
                  />
                )
              );
            })}
            {editableColumns && (
              <TableCell key="settings" alignment={alignment} />
            )}
            {renderActionColumn && (
              <TableCell
                className={styles.actionTableCell}
                key="actions"
                alignment={alignment}
                isNumber={false}
                data-smart-table-action-cell
              >
                <div>{renderActionColumn(item)}</div>
              </TableCell>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

SmartTable.TableCell = SmartTableCell;
